import React, { useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import axios from "axios";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { NetworkConfig } from "src/network/NetworkConfig";
import { Process, STEP_TYPES } from "src/api/Processes";
import { Tiles } from "src/components/tile";
import { ColorStyle } from "src/utils/Colors";
import { useTranslation } from "react-i18next";
import { Route, selectRoute } from "src/config/routes";
import { useUrlQuery } from "src/utils/Route";
import styles from "./Start.module.css";
import { Person } from "src/api/Notification";
import { v4 as uuidv4 } from "uuid";
import {
    INSERT_POSITION,
    insertStepIntoAllProcesses,
    InsertStepIntoAllProcessesArguments
} from "src/utils/ProcessHelper";
import i18n, { getLang, getBrowserLanguage } from "src/utils/i18n";
import { useLanguageDropdownItems } from "src/hooks/translation/useLanguageDropdownItems";

const StartBase = () => {
    const { t } = useTranslation();
    const { navStore, uiStore, easyContactStore } = useContext(RootStoreContext);
    const customerName = getComputedStyle(document.documentElement).getPropertyValue("--wwcustomername");
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const { languageDropdownItems } = useLanguageDropdownItems();

    let urlQuery = useUrlQuery();
    const qrCodeToken = urlQuery.get("t");

    useEffect(() => {
        runInAction(() => {
            navStore.setTitle("EasyContact");
            navStore.setBackbutton(null);
            uiStore.setBackgroundColor(ColorStyle("white"));
        });

        axios
            .get(`${NetworkConfig.getInfoUrl}${qrCodeToken ? `?t=${qrCodeToken}` : ""}`)
            .then((response) => {
                setIsLoading(true);
                if (response.status === 200 && response.data && response.data.active === true) {
                    const tokenvalid: boolean = response.data.tokenvalid;

                    const hasMultilingualOption = response.data.hasmultilingualoption;

                    if (
                        !easyContactStore.applicationLanguage &&
                        hasMultilingualOption &&
                        !uiStore.displayChangeLanguageDropdown
                    ) {
                        i18n.changeLanguage(getBrowserLanguage());

                        uiStore.setCurrentLanguageDropdownValue(
                            languageDropdownItems.find(
                                (languageDropdownItem) => languageDropdownItem.value === getBrowserLanguage()
                            )?.label ?? ""
                        );
                        uiStore.setDisplayChangeLanguageDropdown(true);

                        easyContactStore.setApplicationLanguage(getBrowserLanguage());
                    } else if (easyContactStore.applicationLanguage && !hasMultilingualOption) {
                        easyContactStore.setApplicationLanguage(undefined);
                        i18n.changeLanguage("de");
                        uiStore.setDisplayChangeLanguageDropdown(false);
                    }

                    const backendPersonsArray: Person[] = [];

                    if (tokenvalid) {
                        const realestateName = response.data.realestatename;

                        if (Array.isArray(response.data.persons) && response.data.persons.length > 0) {
                            response.data.persons.forEach((person: any) => {
                                const backendPerson = {
                                    id: person.id ?? undefined,
                                    salutation: person.salutation ?? undefined,
                                    name1: person.name1 ?? undefined,
                                    name2: person.name2 ?? undefined,
                                    email: person.email ?? undefined,
                                    mobile: person.phone ?? undefined,
                                    gender: person.gender ?? undefined
                                };
                                backendPersonsArray.push(backendPerson);
                            });

                            easyContactStore.setBackendAvailableContactPersons(backendPersonsArray);
                            easyContactStore.setBackendContactInformation(undefined);
                        }

                        navStore.setTitle(realestateName);
                    } else {
                        easyContactStore.setBackendAvailableContactPersons(undefined);
                        easyContactStore.setBackendContactInformation(undefined);
                    }

                    axios.get(NetworkConfig.getProcessesUrl).then((response) => {
                        runInAction(() => {
                            easyContactStore.setNewNotification();

                            let processes: Process[] = response.data.ec_processes;
                            const increaseUtilitiesAdvanceProcess = processes.find((process) =>
                                process.process_mls.some(
                                    (processMl) =>
                                        processMl.language === "de" && processMl.title?.trim() === "Akontozahlung erhöhen"
                                )
                            );

                            if (increaseUtilitiesAdvanceProcess) {
                                const requiredLanguages = ["de", "en", "fr", "it"];
                                requiredLanguages.forEach((language) => {
                                    const hasTranslation = increaseUtilitiesAdvanceProcess.process_mls.some(
                                        (processMl) => processMl.language === language && !!processMl.title?.trim()
                                    );

                                    if (!hasTranslation) {
                                        increaseUtilitiesAdvanceProcess.process_mls.push({
                                            language,
                                            title: t("processes.increase_utilities_advance", { lng: language })
                                        });
                                    }
                                });
                            }

                            if (backendPersonsArray.length > 0) {
                                insertStepIntoAllProcesses({
                                    processes: processes,
                                    stepToInsert: {
                                        id: uuidv4(),
                                        type: STEP_TYPES.CONTACT_PERSONS,
                                        step_mls: [
                                            {
                                                language: "de",
                                                title: "",
                                                hint: t("screens.contact.contactperson_selection", { lng: "de" })
                                            },
                                            {
                                                language: "en",
                                                title: "",
                                                hint: t("screens.contact.contactperson_selection", { lng: "en" })
                                            },
                                            {
                                                language: "fr",
                                                title: "",
                                                hint: t("screens.contact.contactperson_selection", { lng: "fr" })
                                            },
                                            {
                                                language: "it",
                                                title: "",
                                                hint: t("screens.contact.contactperson_selection", { lng: "it" })
                                            }
                                        ]
                                    },
                                    referenceStepType: STEP_TYPES.CONTACT,
                                    insertPosition: INSERT_POSITION.BEFORE
                                });
                            }

                            if (!tokenvalid) {
                                const insertStepArguments: InsertStepIntoAllProcessesArguments = {
                                    processes: processes,
                                    stepToInsert: {
                                        id: uuidv4(),
                                        type: STEP_TYPES.REALESTATE,
                                        step_mls: [
                                            {
                                                language: "de",
                                                title: t("screens.realestate.title", { lng: "de" }),
                                                hint: t("screens.realestate.hint", { lng: "de" })
                                            },
                                            {
                                                language: "en",
                                                title: t("screens.realestate.title", { lng: "en" }),
                                                hint: t("screens.realestate.hint", { lng: "en" })
                                            },
                                            {
                                                language: "fr",
                                                title: t("screens.realestate.title", { lng: "fr" }),
                                                hint: t("screens.realestate.hint", { lng: "fr" })
                                            },
                                            {
                                                language: "it",
                                                title: t("screens.realestate.title", { lng: "it" }),
                                                hint: t("screens.realestate.hint", { lng: "it" })
                                            }
                                        ]
                                    },
                                    insertPosition: INSERT_POSITION.BEFORE
                                };

                                if (backendPersonsArray.length > 0) {
                                    insertStepArguments["referenceStepType"] = STEP_TYPES.CONTACT_PERSONS;
                                    insertStepIntoAllProcesses(insertStepArguments);
                                } else {
                                    insertStepArguments["referenceStepType"] = STEP_TYPES.CONTACT;
                                    insertStepIntoAllProcesses(insertStepArguments);
                                }
                            }

                            easyContactStore.setProcesses(processes);
                        });
                    });
                    setIsLoading(false);
                } else {
                    navStore.setActive(false);
                }
            })
            .catch(() => {
                navStore.setActive(false);
                setIsLoading(false);
            });

        return () => {
            runInAction(() => {
                navStore.reset();
            });
        };
        // Only run use effect one time (only load processes one time)
        // eslint-disable-next-line
    }, []);

    if (!easyContactStore.processes || isLoading) {
        return <Screens.Loading noBackgroundChange />;
    }

    return (
        <UI.Container fluid className="d-flex flex-grow-1 flex-column">
            <UI.Row className="flex-grow-1">
                <UI.Col md={3} lg={4} className="d-flex align-items-center justify-content-center p-4">
                    <img src={NetworkConfig.brandAssets.logoImage} className={styles.CustomerLogo} alt="Logo" />
                </UI.Col>
                <UI.Col
                    md={9}
                    lg={8}
                    className={`d-flex align-items-center justify-content-center py-4 ${styles.Container}`}
                >
                    <UI.Container>
                        <UI.Row>
                            <UI.Col
                                xs={12}
                                sm={{ span: 10, offset: 1 }}
                                lg={{ span: 8, offset: 2 }}
                                className="text-center"
                            >
                                <p className={`${styles.WelcomeText} font-weight-bold`}>
                                    {t("screens.start.salutation1", { company: customerName })}
                                </p>
                                <p>{t("screens.start.salutation2")}</p>
                                <Tiles.Container>
                                    {easyContactStore.processes.map((process) => {
                                        const currentLanguage = getLang();
                                        const label = process.process_mls.find(
                                            (process) => process.language === currentLanguage
                                        )?.title;

                                        return (
                                            <Tiles.Tile
                                                key={process.id}
                                                icon={process.icon}
                                                label={label ?? ""}
                                                to={selectRoute(Route.process, { processId: process.id })}
                                                id={process.id}
                                            />
                                        );
                                    })}
                                </Tiles.Container>
                            </UI.Col>
                        </UI.Row>
                    </UI.Container>
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const Start = Sentry.withProfiler(observer(StartBase));
